import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, 
  Target, 
  FileText, 
  Activity,
  Plus,
  Settings,
  User,
  Home,
  ArrowRight,
  TrendingUp,
  Calendar,
  Eye,
  Search,
  Filter
} from 'lucide-react'
import { mockStrategies } from '../utils/mockData'
import Navigation from '../components/Navigation'
import Toast from '../components/Toast'

export default function DemoHomePage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [showToast, setShowToast] = useState(false)

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const filteredStrategies = mockStrategies.filter(strategy => {
    const matchesSearch = strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         strategy.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterType === 'all' || strategy.type === filterType
    return matchesSearch && matchesFilter
  })

  const navigation = [
    { name: '首页', href: '/demo', icon: Home },
    { name: '新建策略', href: '/demo/new-strategy', icon: Plus },
    { name: '策略列表', href: '/demo/strategies', icon: BarChart3 },
    { name: '手动测试', href: '/demo/manual-test', icon: Target },
    { name: '回测分析', href: '/demo/backtest', icon: TrendingUp },
    { name: '实盘追踪', href: '/demo/live-tracking', icon: Activity },
    { name: '研究笔记', href: '/demo/notes', icon: FileText },
    { name: '设置', href: '/demo/settings', icon: Settings },
    { name: '个人中心', href: '/demo/profile', icon: User },
  ]

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gradient mb-2">策略管理 Demo</h2>
          <p className="text-white/70">管理和监控您的交易策略</p>
        </div>

        {/* 搜索和筛选 */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索策略名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-white/50" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-3"
              >
                <option value="all">所有类型</option>
                <option value="trend">趋势跟踪</option>
                <option value="grid">网格交易</option>
                <option value="mean_reversion">均值回归</option>
                <option value="breakout">突破策略</option>
                <option value="scalping">高频交易</option>
                <option value="other">其他</option>
              </select>
            </div>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">总策略数</p>
                <p className="text-2xl font-bold text-gradient">{mockStrategies.length}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">实盘中</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'live').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                <Calendar className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">验证中</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'test').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-gray-500/20 rounded-xl border border-gray-500/30">
                <Eye className="w-6 h-6 text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">草稿</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 策略列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredStrategies.map((strategy) => (
            <div key={strategy.id} className="card hover:scale-105 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-ocean-primary mb-2">
                    {strategy.name}
                  </h3>
                  <p className="text-sm text-ocean-secondary mb-3">
                    {strategy.description}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                  {getStatusLabel(strategy.status)}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-ocean-secondary">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {getTypeLabel(strategy.type)}
                </div>
                <div className="flex items-center text-sm text-ocean-secondary">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {strategy.market_symbol} • {strategy.timeframe}
                </div>
                <div className="flex items-center text-sm text-ocean-secondary">
                  <Calendar className="w-4 h-4 mr-2" />
                  创建于 {new Date(strategy.created_at).toLocaleDateString('zh-CN')}
                </div>
              </div>

              <div className="mt-4 flex space-x-2">
                <button 
                  onClick={() => {
                    setShowToast(true)
                  }}
                  className="flex-1 btn-secondary text-sm"
                >
                  查看详情
                </button>
                <button 
                  onClick={() => {
                    setShowToast(true)
                  }}
                  className="flex-1 btn-primary text-sm"
                >
                  运行回测
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 功能导航 */}
        <div className="card">
          <h3 className="text-xl font-bold text-gradient mb-6">功能导航</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {navigation.slice(1).map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className="flex items-center p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-all duration-200 group"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500/20 to-purple-600/20 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-violet-300" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-ocean-primary">{item.name}</div>
                    <div className="text-xs text-ocean-secondary">点击进入</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-violet-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )
            })}
          </div>
        </div>
      </main>
      
      {/* Toast */}
      <Toast
        message="Demo仅做展示，详细功能请查看下方功能导航"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </div>
  )
} 